import { ISwitchHistoryConfig } from '@abrController/DataCollectors/SwitchHistory/SwitchHistory.config';
import { BufferControllerEvents } from '@bufferController/BufferController.events';
import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { TEventBus } from '@eventBus/EventBus';
import { IResolution } from '@resolutionsManager/IResolutionsManager';
import { ResolutionsManagerEvents } from '@resolutionsManager/ResolutionsManager.events';
import bind from '@utils/decorators/bind';

interface IBannedResolution {
  resolution: IResolution;
  bannedAt: number;
}

export class SwitchHistory extends ConfigConsumer<ISwitchHistoryConfig> {
  private _lastSelectedResolution: IResolution | null = null;
  private _bannedResolutions: IBannedResolution[] = [];

  private readonly _onMetricsReadyListener: string;
  private readonly _onResolutionSelectedListener: string;

  constructor(eventBus: TEventBus) {
    super('switchHistory', eventBus);

    this._onResolutionSelectedListener = this._eventBus.subscribe(ResolutionsManagerEvents.resolutionChanged, this._onResolutionChanged);
    this._onMetricsReadyListener = this._eventBus.subscribe(BufferControllerEvents.bufferMetricsAvailable, this._checkBanedResolutions);
  }

  public destroy(): void {
    super.destroy();

    this._eventBus.unsubscribe(this._onResolutionSelectedListener);
    this._eventBus.unsubscribe(this._onMetricsReadyListener);

    this.reset();
  }

  public reset(): void {
    this._bannedResolutions = [];
  }

  public getBannedResolutions(): IResolution[] {
    return this._bannedResolutions.map(({ resolution }) => resolution);
  }

  @bind
  public _handleConfigUpdated(updatedValues: Partial<ISwitchHistoryConfig>): void {
    if (updatedValues.banResolutionDuration) {
      this._checkBanedResolutions();
    }
  }

  @bind
  private _onResolutionChanged(resolution: IResolution): void {
    if (!this._lastSelectedResolution || this._lastSelectedResolution.bitrate < resolution.bitrate) {
      // We're going to increase resolution
      this._lastSelectedResolution = resolution;
      return;
    }

    if (this._lastSelectedResolution.bitrate > resolution.bitrate) {
      // We're going to decrease resolution
      this._bannedResolutions.push({
        resolution,
        bannedAt: Date.now(),
      });

      this._lastSelectedResolution = resolution;
    }
  }

  @bind
  private _checkBanedResolutions(): void {
    if (!this._bannedResolutions.length) {
      return;
    }

    const now: number = Date.now();

    this._bannedResolutions = this._bannedResolutions.filter(({ bannedAt }) => now - bannedAt < this.config.banResolutionDuration);
  }
}
